import { useState } from 'react';
import { Modal, ModalHeader, Button } from './ui';
import { DLG } from './dialogStyles';
import { resetStudentPassword } from '../data/api';

/* Reset-password modal (Sandy 2026-08-23, moved here from Home). The teacher
   types a code - any characters, 8-20 long, no spaces (the Cognito pool's
   password policy is min-length 8, temp passwords included); the backend sets
   it as a TEMP password (student signs in with it once, then must create a
   new password). Module-level component so the input keeps focus across
   re-renders; remount per student via key={student.userId}. */
export default function ResetPasswordModal({ student, classID, auth, onClose, onReset }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { code, email } after success
  const valid = /^\S{8,20}$/.test(code);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await resetStudentPassword(auth, classID, student.userId, code);
      setDone({ code, email: r.email || null });
      onReset?.(student);
    } catch (e) {
      setError(e?.message || 'Could not reset the password. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="sm">
      <ModalHeader
        title={done ? 'Password reset' : 'Reset password'}
        sub={student.name}
        pad={DLG.head}
        onClose={onClose}
      />
      {done ? (
        <div className={DLG.body}>
          <p className="m-0 text-[13px] text-neutral-700 leading-relaxed">
            Give {student.name} this one-time code — it works as their password the next time they sign in
            {done.email ? <> with <strong>{done.email}</strong></> : null}, and they&apos;ll be asked to create a new password right away.
          </p>
          <div className="mt-[12px] text-center text-[24px] font-extrabold tracking-[0.12em] text-ink break-all select-all">
            {done.code}
          </div>
          <p className="mt-[12px] mb-0 text-[12.5px] text-neutral-500 leading-relaxed">
            The code stops working once they set their new password. This is the only place it&apos;s shown — it isn&apos;t stored anywhere.
          </p>
        </div>
      ) : (
        <div className={DLG.body}>
          <p className="m-0 text-[13px] text-neutral-700 leading-relaxed">
            Pick a code — at least 8 characters, letters or numbers, no spaces. It becomes {student.name}&apos;s
            temporary password: they sign in with it once, then create a new password of their own.
            Their current password stops working immediately.
          </p>
          <input
            type="text"
            autoFocus
            maxLength={20}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, '').slice(0, 20))}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="Code (8+ characters)"
            className="mt-[12px] w-full h-11 px-[12px] rounded-[8px] border-0 ring-1 ring-inset ring-neutral-300 text-center text-[18px] font-bold tracking-[0.12em] text-ink focus:outline-none focus:ring-ink"
          />
          {error && <p className="mt-2 mb-0 text-[12.5px] font-semibold text-error-600">{error}</p>}
        </div>
      )}
      <div className={DLG.foot}>
        {done ? (
          <Button variant="primary" size="dlg" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" size="dlg" onClick={onClose}>Cancel</Button>
            <button
              type="button"
              onClick={submit}
              disabled={!valid || busy}
              className="inline-flex items-center justify-center h-10 px-[16px] whitespace-nowrap cursor-pointer text-sm font-semibold text-white bg-ink hover:bg-neutral-800 border-0 rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
